import { useState, useEffect, useCallback, useMemo } from "react";
import { Search, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2, X, AppWindow, Cpu } from "lucide-react";
import baseapi from "../services/baseapi";
import { showError } from "../utils/toast";
import { ItemsPerPageDropdown } from "../components/ItemsPerPageDropdown ";
import { useClientLevel } from "../hooks/useClientLevel";
import { motion, AnimatePresence } from "framer-motion";
import ColumnVisibilityDropdown, { type ColumnOption } from "../components/ColumnVisibilityDropdown";

interface AssetInfo {
    asset_id: number;
    name: string;
    hostname: string;
    ip_address: string;
    mac_address: string;
    version?: string;
    install_date?: string;
}

interface Application {
    id: number;
    app_name: string;
    publisher: string;
    asset_count: number;
    assets: string; // JSON string of AssetInfo[]
}

export type AppColumnKey = 'app_name' | 'publisher' | 'asset_count';

export const Applications = () => {
    const level = useClientLevel();
    const [apps, setApps] = useState<Application[]>([]);
    const [loading, setLoading] = useState(true);

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Column visibility state
    const [visibleColumns, setVisibleColumns] = useState<Record<AppColumnKey, boolean>>({
        app_name: true,
        publisher: true,
        asset_count: true,
    });

    const columnOptions: ColumnOption<AppColumnKey>[] = useMemo(() => [
        { key: 'app_name', label: 'App Name' },
        { key: 'publisher', label: 'Publisher' },
        { key: 'asset_count', label: 'Asset Count' },
    ], []);

    const visibleColsCount = Object.values(visibleColumns).filter(Boolean).length || 1;

    // Main Pagination state
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    // Drawer state
    const [selectedApp, setSelectedApp] = useState<Application | null>(null);
    const [assetSearchTerm, setAssetSearchTerm] = useState("");
    const [assetPage, setAssetPage] = useState<number>(1);
    const [assetLimit, setAssetLimit] = useState<number>(10);

    // Debounce search
    useEffect(() => {
        const handler = setTimeout(() => {
            if (debouncedSearch !== searchTerm) {
                setDebouncedSearch(searchTerm);
                setCurrentPage(1);
            }
        }, 500);

        return () => clearTimeout(handler);
    }, [searchTerm, debouncedSearch]);

    const fetchApplications = useCallback(async (skip = 0, limit = itemsPerPage) => {
        if (!level?.id) {
            setApps([]);
            setTotalItems(0);
            setTotalPages(0);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const queryObj: any = {};
            if (debouncedSearch) {
                queryObj.$or = [
                    { app_name: { $ilike: `%${debouncedSearch}%` } },
                    { publisher: { $ilike: `%${debouncedSearch}%` } }
                ];
            }

            const payload: any = {
                skip,
                limit,
                client_id: level.id,
                search: debouncedSearch || undefined,
                query: JSON.stringify(queryObj)
            };

            const response = await baseapi.get("/applications/by_name", { params: payload });

            if (response.data.status) {
                setApps(response.data.data || []);
                setTotalItems(response.data.total || (response.data.data || []).length);
                setTotalPages(Math.ceil((response.data.total || (response.data.data || []).length) / limit));
            } else {
                setApps([]);
                setTotalItems(0);
                setTotalPages(0);
            }
        } catch (error: any) {
            showError(error.message || "Failed to fetch applications");
            setApps([]);
            setTotalItems(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    }, [itemsPerPage, debouncedSearch, level]);

    useEffect(() => {
        fetchApplications((currentPage - 1) * itemsPerPage, itemsPerPage);
    }, [currentPage, itemsPerPage, fetchApplications]);

    const handleRefresh = () => {
        fetchApplications((currentPage - 1) * itemsPerPage, itemsPerPage);
    };

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(1);
    };

    // Parse assets from JSON string safely
    const parsedAssets = useMemo<AssetInfo[]>(() => {
        if (!selectedApp) return [];
        try {
            if (typeof selectedApp.assets === "string") {
                return JSON.parse(selectedApp.assets || "[]");
            } else if (Array.isArray(selectedApp.assets)) {
                return selectedApp.assets;
            }
        } catch (e) {
            console.error("Failed to parse assets string:", e);
        }
        return [];
    }, [selectedApp]);

    // Local filtering for assets inside drawer
    const filteredAssets = useMemo(() => {
        const query = assetSearchTerm.toLowerCase().trim();
        if (!query) return parsedAssets;

        return parsedAssets.filter((asset) => {
            return (
                (asset.name && asset.name.toLowerCase().includes(query)) ||
                (asset.hostname && asset.hostname.toLowerCase().includes(query)) ||
                (asset.ip_address && asset.ip_address.toLowerCase().includes(query)) ||
                (asset.mac_address && asset.mac_address.toLowerCase().includes(query)) ||
                (asset.version && asset.version.toLowerCase().includes(query)) ||
                (asset.install_date && asset.install_date.toLowerCase().includes(query))
            );
        });
    }, [parsedAssets, assetSearchTerm]);

    // Local pagination for assets inside drawer
    const paginatedAssets = useMemo(() => {
        const start = (assetPage - 1) * assetLimit;
        const end = start + assetLimit;
        return filteredAssets.slice(start, end);
    }, [filteredAssets, assetPage, assetLimit]);

    const totalAssetPages = Math.ceil(filteredAssets.length / assetLimit);

    if (!level?.id) {
        return (
            <div className="bg-gray-50 dark:bg-gray-900 p-6 min-h-screen pb-20 flex items-center justify-center">
                <div className="text-center bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-xl p-8 max-w-md w-full shadow-2xl backdrop-blur-sm">
                    <AppWindow className="w-12 h-12 text-orange-500 mx-auto mb-4 animate-pulse" />
                    <h2 className="text-lg font-bold text-gray-900 dark:text-gray-200 uppercase tracking-wider mb-2">No Client Selected</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Please select a client from the bottom dock to view application data.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex p-1 flex-col bg-transparent text-gray-900 dark:text-gray-200 relative overflow-hidden">
            <div
            >
                {/* Top Header Row */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-1 py-2">
                    <div className="flex items-center gap-1.5">
                        <div className="bg-orange-600 p-1.5 rounded shadow-lg">
                            <AppWindow className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <h1 className="font-bold text-lg text-gray-900 dark:text-gray-200 leading-none">Applications</h1>
                        </div>
                        {!loading && totalItems > 0 && (
                            <div className="font-bold text-lg text-gray-700 dark:text-gray-100">
                                ({totalItems})
                            </div>
                        )}
                    </div>

                    <div className="flex items-center flex-wrap gap-2 w-full sm:w-auto">
                        <button
                            onClick={handleRefresh}
                            disabled={loading}
                            className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shrink-0"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <div className="relative flex-1 min-w-[130px] sm:w-56 sm:flex-none">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-3.5 h-3.5" />
                            <input
                                type="text"
                                placeholder="Search apps..."
                                className="w-full bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500 outline-none transition-all"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <ColumnVisibilityDropdown
                            columns={columnOptions}
                            visibleColumns={visibleColumns}
                            onChange={setVisibleColumns}
                        />
                    </div>
                </div>

                {/* Main Table Container */}
                <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden mt-1">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs border-collapse">
                            <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10">
                                <tr className="border-b border-gray-200 dark:border-gray-700/50">
                                    {visibleColumns.app_name && (
                                        <th className="px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest">
                                            App Name
                                        </th>
                                    )}
                                    {visibleColumns.publisher && (
                                        <th className="px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest">
                                            Publisher
                                        </th>
                                    )}
                                    {visibleColumns.asset_count && (
                                        <th className="px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest">
                                            Asset Count
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700/30">
                                {loading ? (
                                    <tr>
                                        <td colSpan={visibleColsCount} className="py-16 text-center text-gray-600 dark:text-gray-100 font-bold uppercase tracking-widest italic">
                                            <div className="flex items-center justify-center gap-2">
                                                <Loader2 className="w-4 h-4 animate-spin text-orange-500" /> Loading applications
                                            </div>
                                        </td>
                                    </tr>
                                ) : apps.length === 0 ? (
                                    <tr>
                                        <td colSpan={visibleColsCount} className="py-16 text-center text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest italic">
                                            <div className="flex flex-col items-center gap-2">
                                                <AppWindow className="w-6 h-6 text-gray-400 dark:text-gray-600" /> No applications found.
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    apps.map((app) => (
                                        <tr
                                            key={app.id}
                                            onClick={() => {
                                                setSelectedApp(app);
                                                setAssetSearchTerm("");
                                                setAssetPage(1);
                                            }}
                                            className="hover:bg-gray-50/80 dark:hover:bg-gray-800/60 transition-colors group border-b border-gray-100 dark:border-gray-700/30 cursor-pointer"
                                        >
                                            {visibleColumns.app_name && (
                                                <td className="px-4 py-3 font-bold text-gray-900 dark:text-gray-100 text-[11px] font-mono">
                                                    {app.app_name}
                                                </td>
                                            )}
                                            {visibleColumns.publisher && (
                                                <td className="px-4 py-3 text-gray-600 dark:text-gray-300 text-[10px]">
                                                    {app.publisher || <span className="text-gray-400 dark:text-gray-500 italic">Unknown Publisher</span>}
                                                </td>
                                            )}
                                            {visibleColumns.asset_count && (
                                                <td className="px-4 py-3">
                                                    <span
                                                        className="inline-flex items-center justify-center px-2 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/20 dark:hover:bg-orange-500/20 dark:hover:text-orange-300 font-bold text-[10px] transition-colors"
                                                    >
                                                        {app.asset_count || 0}
                                                    </span>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="bg-white dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-6 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <ItemsPerPageDropdown
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                            />
                            <span className="text-[10px] text-gray-700 dark:text-gray-100 font-bold uppercase tracking-widest">
                                <span className="text-gray-900 dark:text-gray-100">
                                    {totalItems === 0 ? 0 : startIndex + 1}
                                </span>
                                {" - "}
                                <span className="text-gray-900 dark:text-gray-100">
                                    {Math.min(endIndex, totalItems)}
                                </span>
                                {" OF "}
                                {totalItems}
                            </span>
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                className="p-1.5 text-gray-600 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white disabled:opacity-30"
                            >
                                <ChevronsLeft className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="p-1.5 text-gray-600 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white disabled:opacity-30"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>

                            <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-lg shadow-orange-900/20">
                                {currentPage}
                            </div>

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className="p-1.5 text-gray-600 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white disabled:opacity-30"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className="p-1.5 text-gray-600 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white disabled:opacity-30"
                            >
                                <ChevronsRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sliding Drawer for Assets List */}
            <AnimatePresence>
                {selectedApp && (
                    <>
                        {/* Backdrop Overlay */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 pointer-events-auto"
                            onClick={() => setSelectedApp(null)}
                        />

                        {/* Drawer Panel */}
                        <motion.div
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "spring", damping: 30, stiffness: 300 }}
                            className="fixed top-0 right-0 h-full w-[35%] min-w-[360px] max-w-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col z-[100] p-4 text-gray-900 dark:text-white"
                        >
                            {/* Drawer Header */}
                            <div className="flex justify-between items-start mb-4 border-b border-gray-200 dark:border-gray-800 pb-4 shrink-0 bg-gray-50/50 dark:bg-gray-900/50">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-orange-50 dark:bg-orange-600/30 border border-orange-200 dark:border-orange-500/30 rounded-xl text-orange-600 dark:text-orange-400">
                                        <Cpu size={18} />
                                    </div>
                                    <div>
                                        <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-widest">
                                            Linked Assets
                                        </h2>
                                        <span className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 font-mono truncate max-w-[250px] block" title={selectedApp.app_name}>
                                            {selectedApp.app_name}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSelectedApp(null)}
                                    className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800 rounded-lg transition-colors"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            {/* Local Search Input */}
                            <div className="relative mb-4">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-3.5 h-3.5" />
                                <input
                                    type="text"
                                    placeholder="Search linked assets by name, hostname, IP..."
                                    className="w-full bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500 outline-none transition-all"
                                    value={assetSearchTerm}
                                    onChange={(e) => {
                                        setAssetSearchTerm(e.target.value);
                                        setAssetPage(1);
                                    }}
                                />
                            </div>

                            <div className="flex items-center justify-between px-1 mb-2">
                                <span className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest">
                                    Assets List
                                </span>
                                <span className="text-[10px] text-gray-500 dark:text-gray-500 font-bold">
                                    {filteredAssets.length} ASSET{filteredAssets.length !== 1 ? 'S' : ''}
                                </span>
                            </div>

                            {/* Drawer Card Content */}
                            <div className="flex-1 overflow-y-auto custom-scroll space-y-3 min-h-0 pr-1">
                                {paginatedAssets.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-48 gap-3 opacity-40">
                                        <Cpu className="w-8 h-8 text-gray-400 animate-pulse" />
                                        <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                                            No assets found
                                        </span>
                                    </div>
                                ) : (
                                    paginatedAssets.map((asset, index) => {
                                        const formatInstallDate = (dateStr?: string) => {
                                            if (!dateStr) return <span className="text-gray-400 dark:text-gray-600 italic">-</span>;
                                            const date = new Date(dateStr);
                                            return isNaN(date.getTime()) ? dateStr : date.toLocaleDateString();
                                        };

                                        return (
                                            <div
                                                key={asset.asset_id || index}
                                                className="flex flex-col p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/40 hover:bg-gray-100/60 dark:hover:bg-gray-800/60 transition-colors"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-start justify-between mb-1.5 gap-2">
                                                        <h3 className="text-xs font-bold text-gray-900 dark:text-gray-100 truncate" title={asset.name || asset.hostname}>
                                                            {asset.name || asset.hostname || "-"}
                                                        </h3>
                                                        <div className="flex flex-wrap gap-1 shrink-0 justify-end max-w-[55%]">
                                                            {asset.ip_address && (
                                                                <span className="text-[8px] font-mono text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-gray-900 px-1.5 py-0.5 rounded border border-purple-200 dark:border-gray-800">
                                                                    IP: {asset.ip_address}
                                                                </span>
                                                            )}
                                                            {asset.mac_address && (
                                                                <span className="text-[8px] font-mono text-cyan-700 dark:text-cyan-400 bg-cyan-50 dark:bg-gray-900 px-1.5 py-0.5 rounded border border-cyan-200 dark:border-gray-800">
                                                                    MAC: {asset.mac_address}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-gray-200 dark:border-gray-800">
                                                        {/* Hostname */}
                                                        <div className="flex flex-col min-w-0">
                                                            <span className="text-[8px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-0.5">
                                                                Hostname
                                                            </span>
                                                            <span className="text-[10px] font-mono text-lime-700 dark:text-lime-300 truncate" title={asset.hostname}>
                                                                {asset.hostname || "-"}
                                                            </span>
                                                        </div>

                                                        {/* Installed Version */}
                                                        <div className="flex flex-col">
                                                            <span className="text-[8px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-0.5">
                                                                Version
                                                            </span>
                                                            <span className="text-[10px] font-mono text-orange-600 dark:text-orange-300 truncate" title={asset.version}>
                                                                {asset.version || "-"}
                                                            </span>
                                                        </div>

                                                        {/* Install Date */}
                                                        <div className="flex flex-col">
                                                            <span className="text-[8px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-0.5">
                                                                Install Date
                                                            </span>
                                                            <span className="text-[10px] font-mono text-emerald-700 dark:text-emerald-300 truncate">
                                                                {formatInstallDate(asset.install_date)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {/* Drawer Footer Pagination */}
                            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4 flex items-center justify-between shrink-0 bg-gray-50/50 dark:bg-gray-900/50">
                                <div className="flex items-center gap-3">
                                    <span className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wide">
                                        Show:
                                    </span>
                                    <select
                                        className="px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-white text-[10px] focus:outline-none focus:border-orange-500"
                                        value={assetLimit}
                                        onChange={(e) => {
                                            setAssetLimit(Number(e.target.value));
                                            setAssetPage(1);
                                        }}
                                    >
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                    </select>
                                </div>

                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => setAssetPage(1)}
                                        disabled={assetPage === 1}
                                        className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30"
                                    >
                                        <ChevronsLeft className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        onClick={() => setAssetPage((prev) => Math.max(prev - 1, 1))}
                                        disabled={assetPage === 1}
                                        className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30"
                                    >
                                        <ChevronLeft className="w-3.5 h-3.5" />
                                    </button>

                                    <div className="px-2.5 py-0.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-white text-[10px] font-bold mx-1">
                                        {assetPage} / {totalAssetPages || 1}
                                    </div>

                                    <button
                                        onClick={() => setAssetPage((prev) => Math.min(prev + 1, totalAssetPages))}
                                        disabled={assetPage === totalAssetPages || totalAssetPages === 0}
                                        className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30"
                                    >
                                        <ChevronRight className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        onClick={() => setAssetPage(totalAssetPages)}
                                        disabled={assetPage === totalAssetPages || totalAssetPages === 0}
                                        className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30"
                                    >
                                        <ChevronsRight className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Applications;
